/*
==========================================================
AVA VISION UPLOADER
American Global
==========================================================
*/

import {
    setLatestFrame,
} from "./avaVisionContext";

const API_BASE =
    (
        import.meta.env.VITE_API_BASE ||
        "http://127.0.0.1:8000"
    ).trim();

export interface VisionResponse {
    status: string;
    objects: any[];
    text: string[];
    scene: Record<string, any>;

    vision_status?: {
        code?: string;
        label?: string;
        message?: string;
        tone?: string;
    };

    camera_active?: boolean;
    analysis_completed_at?: number;
    hands_detected?: number;
    gesture_summary?: string;
    wave_detected?: boolean;
    diagnostics_available?: boolean;
}

export interface VisionFaceBox {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

export interface VisionFaceEvidence {
    detected: boolean;
    box?: VisionFaceBox;
    frame_width: number;
    frame_height: number;
    captured_at: number;
    source: "mediapipe_face_landmarker";
}

const TOKEN_KEYS = [
    "ava_session_token",
    "ava_token",
    "token",
    "access_token",
    "authToken",
    "jwt",
] as const;

function readVisionToken(
    storage: Storage | null
): string {

    if (!storage) return "";

    for (const key of TOKEN_KEYS) {
        try {
            const raw = String(
                storage.getItem(key) || ""
            ).trim();

            const token = raw
                .replace(/^Bearer\s+/i, "")
                .trim();

            if (
                token &&
                ![
                    "undefined",
                    "null",
                    "none",
                ].includes(token.toLowerCase())
            ) {
                return token;
            }
        } catch {
            // Continue to the next storage location.
        }
    }

    return "";
}

function getVisionBearerToken(): string {

    if (typeof window === "undefined") {
        return "";
    }

    return (
        readVisionToken(window.localStorage) ||
        readVisionToken(window.sessionStorage)
    );
}

let uploadInProgress = false;

export async function uploadVisionFrame(
    image: string,
    faceEvidence?: VisionFaceEvidence | null
): Promise<VisionResponse | null> {

    if (!image) return null;

    if (uploadInProgress) {
        return null;
    }

    uploadInProgress = true;

    // This is the exact frame displayed in the user preview.
    setLatestFrame(image);

    try {
        const token =
            getVisionBearerToken();

        const res = await fetch(
            `${API_BASE}/api/v1/vision/frame`,
            {
                method: "POST",

                // Keep the secure HttpOnly cookie as the
                // browser authentication source of truth.
                credentials: "include",

                headers: {
                    "Content-Type": "application/json",

                    // AVAAIO native/browser fallback when the
                    // cross-subdomain session cookie is absent.
                    ...(
                        token
                            ? {
                                Authorization:
                                    "Bearer " + token,
                            }
                            : {}
                    ),
                },

                body: JSON.stringify({
                    image,

                    // Auxiliary visual corroboration only.
                    // The backend never treats this as identity,
                    // authorization or biometric authentication.
                    face_evidence:
                        faceEvidence || undefined,
                }),
            }
        );

        if (!res.ok) {
            console.warn(
                "[AVA VISION] upload failed",
                res.status
            );

            return null;
        }

        return (
            await res.json()
        ) as VisionResponse;

    } catch (err) {
        console.warn(
            "[AVA VISION]",
            err
        );

        return null;

    } finally {
        uploadInProgress = false;
    }
}
